import { useState, useEffect, useContext, useRef, useMemo } from "react"
import { MDBAlert, MDBBtn, MDBCard, MDBCardBody, MDBCol, MDBContainer, MDBFooter, MDBIcon,  MDBRow, MDBStepper, MDBStepperStep,  } from "mdb-react-ui-kit"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom";
import { generateClient } from 'aws-amplify/api';
import { UserContext } from "../../../App";
import { useNavigate } from "react-router-dom";
import { hotjar } from "react-hotjar";
import { captureError } from "../../../utils/sentryHelpers";
import { normalizeCountryCode } from "../../../utils/countryCode";

import SideCart from "./SideCart";
import Address from "./Address";
import Payment from "./Payment";
import Confirmation from "./Confirm";
import { fetchAuthSession } from "aws-amplify/auth";
import { useIndustry } from "../../../utils/industryContextHook";
import { logError } from "../../../utils/logging";
import { buildCheckoutProducts } from "../../../utils/boothMap";
import { fetchEventBoothMap } from "../../../utils/boothBookingApi";
import {
  BOOTH_SELECTION_ERROR_CODES,
  applyBoothMapPrices,
  hasBoothParams,
  deriveCustomerType,
  discountAmountFor,
  orderErrorText,
  orderLinesFor,
  parseOrderError,
} from "../../event-management/booking/interactive/checkoutBooths";

async function getIdToken() {
  const { idToken } = (await fetchAuthSession()).tokens ?? {};
  console.log("idToken", idToken)
  return idToken;
}

// a query param that occurs once is a string, several times an array (extractExistingParams)
const paramList = (value) => (Array.isArray(value) ? value : value ? [value] : []).filter((v) => typeof v === "string" && v);

export default function Checkout() {
  const { currentIndustry } = useIndustry();
    const client = generateClient()
    const { t, i18n } = useTranslation()
    const { user } = useContext(UserContext)
    const navigate = useNavigate()

    const prevRef = useRef(null);
    const nextRef = useRef(null);

    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    // parseOrderError result { code, label, duplicate, message } | null
    const [orderError, setOrderError] = useState(null);
    const [params, setParams] = useState({})

    const [products, setProducts] = useState([]);
    const [orderData, setOrderData] = useState({
      buyerOrderNumber: null,
      billingEmail: null,
      vatInfo: {
        vatNumber: null
      },
      countryCode: null,
    });
    const [taxRate, setTaxRate] = useState(19);
    const [customerType, setCustomerType] = useState("private");
    const [eventBooth, setEventBooth] = useState({});
    const [billingAddress, setBillingAddress] = useState({});
    const [personalAddress, setPersonalAddress] = useState({});
    const [useProfileAddressForBilling, setUseProfileAddressForBilling] = useState(true);
    useEffect(() => {
      // Set Tax rate, if customer is business - if country is Germany, set 19% tax rate, otherwise 0%. country is personalAddress.countryCode or billingAddress.countryCode if useProfileAddressForBilling is false
      if(customerType === "business") {
        const country = useProfileAddressForBilling ? personalAddress.countryCode : billingAddress.countryCode;
        if(country === "DE") {
          setTaxRate(19);
        } else {
          setTaxRate(0);
        }
      } else {
        // If customer is private and country is in EU, set 19% tax rate, otherwise 0%
        const country = useProfileAddressForBilling ? personalAddress.countryCode : billingAddress.countryCode;
        const euCountries = ["AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GR", "HR", "HU", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK"];
        if(euCountries.includes(country)) {
          setTaxRate(19);
        } else {
          setTaxRate(0);
        }
      }
    }, [customerType, personalAddress, billingAddress, useProfileAddressForBilling]);

    useEffect(() => {
        if(user) {
          setPersonalAddress({
            salutation: user.salutation,
            title: user.title,
            firstName: user.firstName,
            lastName: user.lastName,
            street: user.contact?.address?.street || "",
            streetNumber: user.contact?.address?.streetNumber || "",
            zip: user.contact?.address?.zip || "",
            city: user.contact?.address?.city || "",
            countryCode: normalizeCountryCode(user.contact?.address?.countryCode, t),
            company: user.contact?.address?.company || ""
          });
        }
      }, [user]);

    const handleClickNext = () => {
    nextRef.current.click();
  }

  const handleClickPrev = () => {
    prevRef.current.click();
  }


    // Handle Query Params
    const [paramsInitialized, setParamsInitialized] = useState(false);
    const [routerParams, setRouterParams] = useSearchParams();
    const extractExistingParams = (searchParams) => {
        return Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
            if (value === 'true') {
                acc[key] = true;
            } else if (value === 'false') {
                acc[key] = false;
            } else {
              if(acc.hasOwnProperty(key) && Array.isArray(acc[key])) {
                acc[key].push(value);
              } else if(acc.hasOwnProperty(key)) {
                acc[key] = [acc[key], value];
              } else {
                acc[key] = value;
              }
            }
            return acc;
        }, {});
    }
    useEffect(() => {
        if(!paramsInitialized) {
          console.log("routerParams", routerParams.getAll('productId'));
        const paramsQuery = extractExistingParams(routerParams);
        console.log("paramsQuery", paramsQuery);
        setParams(paramsQuery);
        setParamsInitialized(true);
        hotjar.event("Checkout initialized");
        }
    }, [routerParams]);
    useEffect(() => {
        if(paramsInitialized) {
        // replace: keep a single /checkout history entry (a push would add a duplicate
        // entry on load, so browser Back / navigate(-1) would land on Checkout again)
        setRouterParams(params, { replace: true });
        }
    }, [params, paramsInitialized]);

    // Get Product
  const getProduct = async (id, eventId) => {
        try {
            const response = await client.graphql({
                query: /* GraphQL */ `
                query GetProduct($id: ID!, $eventId: ID) {
                  getProduct(id: $id) {
                    id
                    name {
                      language
                      text
                    }
                    description {
                      language
                      text
                    }
                    shortDescription {
                      language
                      text
                    }
                    sku
                    type
                    price
                    eventProducts(filter: {eventEventProductsId: {eq: $eventId}}) {
                      items {
                        price
                      }
                    }
                    image {
                      main {
                        fileName
                        alt
                      }
                      additional {
                        fileName
                        alt
                      }
                      __typename
                    }
                    features {
                      name {
                        language
                        text
                      }
                      description {
                        language
                        text
                      }
                    }
                    files {
                      url
                      s3Path
                      fileName
                      type
                      name
                    }
                    allowedBuyerGroups
                    icon
                    createdAt
                    updatedAt
                    bundleBoothAvaiableAccessoriesId
                    bundleBoothIncludedAccessoriesId
                  }
                }
              `,
                variables: { 
                  id,
                  eventId
                 }
            });
            return response.data.getProduct;
        } catch (error) {
            console.error("Error fetching product", error);
            hotjar.event("Error fetching product");
            captureError(error, {
                feature: 'shop',
                operation: 'fetch-product-for-checkout',
                consoleMessage: 'Error fetching product',
                context: {
                    productId: id,
                    eventId
                }
            });
            return null;
        }
  }

    const getEventBooth = async (id) => {
      try {
        const response = await client.graphql({
            query: /* GraphQL */ `
            query GetBookedBooth($id: ID!) {
              getBookedBooth(id: $id) {
                id
                event {
                  id
                  name {
                    language
                    text
                  }
                  image {
                    fileName
                    alt
                  }
                  date
                  startDate
                  endDate
                }
                boothNumber
                status
                createdAt
                updatedAt
              }
            }
          `,
            variables: {
                id
            }
        });
        console.log('getBookedBooth response:', response);
        setEventBooth(response.data.getBookedBooth);
        return response.data.getBookedBooth;
    } catch (error) {
        console.error('getBookedBooth error:', error);
        captureError(error, {
            feature: 'event-management',
            operation: 'fetch-booked-booth-for-checkout',
            consoleMessage: 'getBookedBooth error:',
            context: {
                boothId: id
            }
        });
    }
  }

  // Booth map of the event (getEventBoothMap), fetched once per event/company: the price of each hall-plan
  // booth line comes from it (priceForBooth of the booth; placeOrder sets the binding price).
  const boothMapRef = useRef({ key: null, promise: null });
  const loadBoothMap = (eventId, companyId) => {
    const key = `${eventId}|${companyId || ""}`;
    if (boothMapRef.current.key !== key) {
      const promise = fetchEventBoothMap({ eventId, companyId }).catch((error) => {
        captureError(error, {
          feature: 'shop',
          operation: 'fetch-booth-map-for-checkout',
          consoleMessage: 'Loading the booth map for the checkout prices failed',
          context: { eventId, companyId }
        });
        // the next product load tries again; until then the EventProduct prices are shown
        if (boothMapRef.current.promise === promise) boothMapRef.current = { key: null, promise: null };
        return null;
      });
      boothMapRef.current = { key, promise };
    }
    return boothMapRef.current.promise;
  };

  const fetchProducts = async () => {
    let eventId = null;
    if(params.boothId) {
      const eventBooth = await getEventBooth(params.boothId);
      eventId = eventBooth.event.id;
    } else if(params.eventId) {
      eventId = params.eventId;
    }
    const boothMapPromise = eventId && hasBoothParams(params) ? loadBoothMap(eventId, params.companyId) : Promise.resolve(null);
    // productId params (one per booth or per unit) + booth=<productId>:<boothNumber> params of the hall plan
    const productIds = [...new Set([
      ...paramList(params.productId),
      ...paramList(params.booth).map((booth) => booth.split(":")[0]),
    ].filter(Boolean))];
    const loaded = await Promise.all(productIds.map(async (id) => {
      const product = await getProduct(id, eventId);
      if (!product) return null;
      const eventPrice = product.eventProducts?.items?.[0]?.price;
      if (eventId && eventPrice !== null && eventPrice !== undefined) product.price = eventPrice;
      return product;
    }));
    const productsById = {};
    loaded.forEach((product) => {
      if (product) productsById[product.id] = product;
    });
    // one entry per booth (qty 1 + lineBoothNumber); qty by occurrence for products without booth params
    const entries = buildCheckoutProducts(params, productsById);
    // hall-plan booth lines: price (and its breakdown) of THAT booth from the map, EventProduct price as fallback
    setProducts(applyBoothMapPrices(entries, await boothMapPromise));
  }

    // Get Data
    useEffect(() => {
        if(params.productId || params.booth) {
            fetchProducts();
        }
    }, [params.productId, params.booth]);

    // business-only products switch the checkout to business customers (any product, not just the first)
    useEffect(() => {
        if (deriveCustomerType(products) === "business") setCustomerType("business");
    }, [products]);



    const placeOrder = async () => {
      setIsPlacingOrder(true);
      setOrderError(null);
      try {
          const idToken = await getIdToken();
          // booth products: one line per booth with qty 1 and its boothNumber
          const productsForOrder = orderLinesFor(products, taxRate);

          const totalAmount = products.reduce((acc, product) => {
              return acc + (product.qty && product.qty > 1 ? product.price * product.qty : product.price);
          }, 0);

          let data = {};
          if(params.boothId) {
              data.eventBoothId = params.boothId;
          }
          if(params.eventId) {
              data.eventId = params.eventId;
          }
          if(appliedDiscounts.length > 0) {
              data.discounts = appliedDiscounts
          }

          const response = await fetch('https://y3vayhw2i5bqrm6fpwqz7fzfwe.appsync-api.eu-central-1.amazonaws.com/graphql', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': idToken
              },
              body: JSON.stringify({
                  query: /* GraphQL */ `
                  mutation PlaceOrder(
                    $order: String
                  ) {
                    placeOrder(
                      order: $order
                    )
                  }
                `,
                  variables: {
                      order: JSON.stringify({
                          industryOrdersId: currentIndustry.id,
                          buyerOrderNumber: orderData.buyerOrderNumber ? (orderData.buyerOrderNumber === "not required" ? "" : orderData.buyerOrderNumber) : null,
                          notes: orderData.notes,
                          deliveryAddress: personalAddress,
                          billingAddress: useProfileAddressForBilling ? personalAddress : billingAddress,
                          billingEmail: orderData.billingEmail  ? (orderData.billingEmail === "not required" ? "" : orderData.billingEmail) : null,
                          products: productsForOrder,
                          isBusiness: customerType === "business",
                          vatInfo: orderData.vatInfo.vatNumber ? orderData.vatInfo : null,
                          currency: "EUR",
                          totalAmount: totalAmount + (totalAmount * taxRate / 100),
                          invoiceAmount: totalAmount + (totalAmount * taxRate / 100),
                          invoiceAmountNet: totalAmount,
                          taxRate,
                          paymentProvider: "invoice",
                          ...(params.companyId && { companyOrdersId: params.companyId }),
                          ...(params.adminId && { adminOrdersId: params.adminId }),
                          ...(params.notes && { notes: params.notes }),
                          ...(Object.keys(data).length > 0 && { data: JSON.stringify(data) }),
                      })
                  }
              })
          });
      
          const responseData = await response.json();
          const placedOrderId = responseData?.data?.placeOrder;
          if (responseData?.errors?.length || !placedOrderId) {
              const errorMessage = (responseData?.errors || []).map(e => e?.message).filter(Boolean).join("; ") || "placeOrder returned no order id";
              hotjar.event("Error creating order");
              captureError(new Error(errorMessage), {
                  feature: 'shop',
                  operation: 'place-order',
                  consoleMessage: 'placeOrder returned errors',
                  context: {
                      errors: responseData?.errors,
                      productIds: products.map(p => p.id),
                      eventId: params.eventId,
                      companyId: params.companyId
                  }
              });
              setOrderError(parseOrderError(errorMessage));
              return null;
          }
          hotjar.event("Order placed");
          return placedOrderId;

      } catch (error) {
          console.log("error", error)
          logError({ message: error.message, additionalData: { error } });
          hotjar.event("Error creating order");
          captureError(error, {
              feature: 'shop',
              operation: 'place-order',
              consoleMessage: 'error',
              context: {
                  orderData,
                  products,
                  personalAddress,
                  billingAddress,
                  customerType,
                  taxRate
              }
          });
          setOrderError(parseOrderError(error?.message || ""));
          return null;
      } finally {
          setIsPlacingOrder(false);
      }
  }

    const handleConfirmOrder = async () => {
        const orderId = await placeOrder();
        if(orderId) {
            navigate(`/order/confirmation/${orderId}`);
        }
    }


    // Discount code and handling
    const [discountCode, setDiscountCode] = useState("");
    const [appliedDiscounts, setAppliedDiscounts] = useState([]);
    const [discountErrorMessages, setDiscountErrorMessages] = useState([]);

    // Verwende useMemo anstelle von useEffect für die Berechnung der Discounts
    const discountsWithAmounts = useMemo(() => {
      if (!appliedDiscounts || appliedDiscounts.length === 0) return [];

      // a fixed amount restricted to products counts once per product id (several booths of one product)
      const calculateDiscount = (discount) => discountAmountFor(discount, products);

      return appliedDiscounts.map(discount => {
        const amount = calculateDiscount(discount);
        // Nur ein neues Objekt erstellen, wenn sich der Betrag geändert hat
        if (discount.amount !== amount) {
          return {
            ...discount,
            amount
          };
        }
        return discount;
      });
    }, [appliedDiscounts, products]);

    const applyDiscountCode = async () => {
      try {
        const response = await client.graphql({
          query: /* GraphQL */ `
            query DiscountCodesByCode(
            $code: String!
          ) {
            discountCodesByCode(
              code: $code
            ) {
              items {
                id
                code
                name {
                  language
                  text
                }
                description {
                  language
                  text
                }
                discountType
                discountAmount
                discountPercentage
                maxUsage
                usageCount
                startDate
                endDate
                status
                restrictedProductIds
                canBeCombined
              }
              nextToken
              __typename
            }
          }
        `,
          variables: {
            code: discountCode
          }
        });
        console.log("response", response);
        const discount = response.data.discountCodesByCode.items[0];
        if(discount) {
          // check if discount already applied
          if (appliedDiscounts.find(d => d.id === discount.id)) {
            setDiscountErrorMessages([t("Discount code already applied")]);
            return;
          } else if(discount.startDate && new Date(discount.startDate) > new Date()) {
            setDiscountErrorMessages([t("Discount code is not yet valid")]);
            return;
          } else if(discount.endDate && new Date(discount.endDate) < new Date()) {
            setDiscountErrorMessages([t("Discount code has expired")]);
            return;
          } else if(discount.maxUsage && discount.usageCount >= discount.maxUsage) {
            setDiscountErrorMessages([t("Discount code has reached maximum usage")]);
            return;
          } else {
            setDiscountErrorMessages([]);
            if(discount.canBeCombined) {
              setAppliedDiscounts([...appliedDiscounts, discount]);
            } else {
              setAppliedDiscounts([discount]);
            }
          }
      } else {
        setDiscountErrorMessages([t("Discount code not found")]);
      }
      }
      catch (error) {
        console.error("Error applying discount code", error);
        captureError(error, {
            feature: 'checkout',
            operation: 'apply-discount-code',
            context: {
                discountCode
            }
        });
      }
    }

    const removeDiscountCode = (id) => {
      const updatedDiscounts = appliedDiscounts.filter(discount => discount.id !== id);
      setAppliedDiscounts(updatedDiscounts);
    }

  return (
<>
<div className="d-none">
<MDBBtn ref={prevRef}>{t("Previous")}</MDBBtn>
<MDBBtn ref={nextRef}>{t("Next")}</MDBBtn>
</div>
<MDBRow>
    <MDBCol md="8" xl="8">
    <MDBCard>
        <MDBCardBody className="px-0 px-lg-3">
            <MDBStepper linear type="horizontal" externalNext={nextRef} externalPrev={prevRef}>
                <MDBStepperStep itemId={1} headText={t("Your information")} headIcon={<MDBIcon fas icon="user" />}  >
                    <Address
                    products={products}
                    user={user}
                    customerType={customerType}
                    setCustomerType={setCustomerType}
                    params={params}
                    setParams={setParams}
                    orderData={orderData}
                    setOrderData={setOrderData}
                    useProfileAddressForBilling={useProfileAddressForBilling}
                    setUseProfileAddressForBilling={setUseProfileAddressForBilling}
                    billingAddress={billingAddress}
                    setBillingAddress={setBillingAddress}
                    personalAddress={personalAddress}
                    setPersonalAddress={setPersonalAddress}
                    handleClickPrev={handleClickPrev}
                    handleClickNext={handleClickNext}
                    // validations={validations}
                    // showValidations={showValidations}
                    // setShowValidations={setShowValidations}

                    />
                </MDBStepperStep>
                <MDBStepperStep itemId={2} headText={t("Payment")} headIcon={<MDBIcon fas icon="credit-card" />} >
                    <Payment
                        handleClickPrev={handleClickPrev}
                        handleClickNext={handleClickNext}
                    />
                </MDBStepperStep>
                <MDBStepperStep itemId={3} headText={t("Confirmation")} headIcon={<MDBIcon fas icon="check" />} >
                <Confirmation
                user={user}
                customerType={customerType}
                products={products}
                taxRate={taxRate}
                params={params}
                personalAddress={personalAddress}
                setPersonalAddress={setPersonalAddress}
                useProfileAddressForBilling={useProfileAddressForBilling}
                setUseProfileAddressForBilling={setUseProfileAddressForBilling}
                billingAddress={billingAddress}
                setBillingAddress={setBillingAddress}
                handleConfirmOrder={handleConfirmOrder}
                bookingInProgress={false}
                handleClickPrev={handleClickPrev}
                handleClickNext={handleClickNext}
                isPlacingOrder={isPlacingOrder}
                discountCode={discountCode}
                setDiscountCode={setDiscountCode}
                applyDiscountCode={applyDiscountCode}
                removeDiscountCode={removeDiscountCode}
                appliedDiscounts={discountsWithAmounts} // Verwende hier die berechnete Version mit Beträgen
                discountErrorMessages={discountErrorMessages}
                // validations={validations}
                // showValidations={showValidations}
                // setShowValidations={setShowValidations}
                // errors={errors}
                />
                {orderError && (
                  <MDBAlert open color="danger" className="mt-3 mb-0">
                    <div>
                      {orderErrorText(t, orderError)}
                    </div>
                    {BOOTH_SELECTION_ERROR_CODES.includes(orderError.code) && (
                      <MDBBtn
                        color="danger"
                        outline
                        size="sm"
                        className="mt-2"
                        // hall-plan orders (booth params) go back to the map; legacy list orders return to the page they came from
                        onClick={() => (params.booth && params.companyId && params.eventId
                          ? navigate(`/mycompany/${params.companyId}/select-booth-map/${params.eventId}`)
                          : navigate(-1))}
                      >
                        {t("Back to booth selection")}
                      </MDBBtn>
                    )}
                  </MDBAlert>
                )}
                </MDBStepperStep>
            </MDBStepper>
        </MDBCardBody>
    </MDBCard>
        
    </MDBCol>
    <MDBCol md="4" xl="4">
        <SideCart
        params={params}
        customerType={customerType}
        taxRate={taxRate}
        products={products}
        hideDescription={true}
        showDiscountCode={true}
        discountCode={discountCode}
        setDiscountCode={setDiscountCode}
        applyDiscountCode={applyDiscountCode}
        removeDiscountCode={removeDiscountCode}
        appliedDiscounts={discountsWithAmounts} // Verwende hier die berechnete Version mit Beträgen
        discountErrorMessages={discountErrorMessages}
        />
    </MDBCol>
</MDBRow>
 <MDBFooter className='text-center text-white border-top mt-5'>
      <MDBContainer className='pt-2'>
        <section>
          <MDBBtn
            rippleColor="dark"
            color='link'
            className='text-dark m-1'
            href='https://cleanroom-processes.de/widerrufsrecht/'
            target="_blank"
            role='button'
          >
            {t("Right of withdrawal")}
          </MDBBtn>

          <MDBBtn
            rippleColor="dark"
            color='link'
            className='text-dark m-1'
            href='https://cleanroom-processes.de/datenschutzerklarung/'
            target="_blank"
            role='button'
          >
             {t("Data protection")}
          </MDBBtn>

          <MDBBtn
            rippleColor="dark"
            color='link'

            className='text-dark m-1'
            href='https://cleanroom-processes.de/agb-cleanroom-processes/'
            target="_blank"
            role='button'
          >
             {t("Terms and Conditions")}
          </MDBBtn>

          <MDBBtn
            rippleColor="dark"
            color='link'
            className='text-dark m-1'
            href='https://cleanroom-processes.de/impressum/'
            target="_blank"
            role='button'
          >
             {t("Imprint")}
          </MDBBtn>
        </section>
      </MDBContainer>

    </MDBFooter>
</>
  )
}